import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs.tsx";
import { Button } from "@/components/ui/button.tsx";
import { getCandidate } from "@/api/candidates";
import CandidateGeneral from "./candidates/CandidateGeneral";
import CandidateCompliances from "./candidates/CandidateCompliances.jsx";
import CandidateQuestionnaire from "./candidates/CandidateQuestionnaire.jsx";
import CandidateWorkHistory from "./candidates/CandidateWorkHistory.jsx";
import CandidateTermsAndAgreements from "@/pages/admin/candidates/CandidateTermsAndAgreements.jsx";
import CandidateReferences from "@/pages/admin/candidates/CandidateReferences.jsx";
import CandidateCV from "@/pages/admin/candidates/CandidateCV.jsx";
import { ArrowLeft } from "lucide-react";

export default function CandidateDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [activeTab, setActiveTab] = useState("general");
    const [candidateData, setCandidateData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {

        const hash = location.hash.replace("#", "");
        if (hash) setActiveTab(hash);
    }, [location.hash]);

    useEffect(() => {
        const fetchCandidate = async () => {
            try {
                setLoading(true);
                const data = await getCandidate(slug);
                setCandidateData(data);
            } catch (err) {
                setError("Failed to load candidate.");
            } finally {
                setLoading(false);
            }
        };
        fetchCandidate();
    }, [slug]);

    const handleTabChange = (value) => {
        setActiveTab(value);
        navigate(`#${value}`, { replace: true });
    };

    const updateWorkHistories = (updatedEntry) => {
        setCandidateData((prev) => ({
            ...prev,
            workHistories: prev.workHistories.some(entry => entry.slugId === updatedEntry.slugId)
                ? prev.workHistories.map(entry =>
                    entry.slugId === updatedEntry.slugId ? updatedEntry : entry
                )
                : [...prev.workHistories, updatedEntry]
        }));
    };

    if (loading) return <p className="p-6">Loading candidate details...</p>;
    if (error) return <p className="p-6 text-red-600">Error: {error}</p>;

    return (
        <div className="p-6">
            <Button
                onClick={() => navigate("/admin")}
                className="bg-gray-200 hover:bg-gray-300 text-gray-900 flex items-center space-x-2 px-4 py-2 rounded-md"
            >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Candidates</span>
            </Button>

            <h2 className="text-2xl font-bold mb-4">Candidate Details</h2>
            <p className="text-gray-600 mb-4">
                Viewing details for <strong>{candidateData?.firstName} {candidateData?.lastName}</strong>
                {candidateData?.mainSkill?.name && <span> - {candidateData.mainSkill.name}</span>}
            </p>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="flex space-x-4 border-b pb-2">
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="compliances">Compliances</TabsTrigger>
                    <TabsTrigger value="questionnaire">Questionnaire</TabsTrigger>
                    <TabsTrigger value="work-history">Work History</TabsTrigger>
                    <TabsTrigger value="terms">Terms & Agreements</TabsTrigger>
                    <TabsTrigger value="references">References</TabsTrigger>
                    <TabsTrigger value="cv">CV</TabsTrigger>
                </TabsList>

                <TabsContent value="general">
                    <CandidateGeneral data={candidateData} />
                </TabsContent>
                <TabsContent value="compliances">
                    <CandidateCompliances
                        candidateId={slug}
                        compliances={candidateData?.candidateCompliances}
                        updateCandidateData={setCandidateData}
                    />
                </TabsContent>
                <TabsContent value="questionnaire">
                    <CandidateQuestionnaire
                        updateCandidateData={setCandidateData}
                        candidateId={slug}
                        questionnaireResponses={candidateData?.candidateQuestionnaireResponses}
                    />
                </TabsContent>
                <TabsContent value="work-history">
                    <CandidateWorkHistory
                        candidateId={slug}
                        workHistory={candidateData?.workHistories}
                        updateCandidateData={setCandidateData}
                    />
                </TabsContent>
                <TabsContent value="terms">
                    <CandidateTermsAndAgreements
                        candidateId={candidateData?.id}
                        candidateName={`${candidateData?.firstName} ${candidateData?.lastName}`}
                    />
                </TabsContent>
                <TabsContent value="references">
                    <CandidateReferences
                        candidateId={candidateData?.id}
                        candidateName={`${candidateData?.firstName} ${candidateData?.lastName}`}
                    />
                </TabsContent>
                <TabsContent value="cv">
                    <CandidateCV
                        candidateId={candidateData?.slugId}
                        candidateName={`${candidateData?.firstName} ${candidateData?.lastName}`}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
